import {
  timestamp,
  useCounter,
  useEventListener,
  useIdle,
  useTimestamp,
} from '@vueuse/core';
import { computed, inject, ref, watch } from 'vue';

import { preloadRouteComponents } from '../01.router';

import useCookie from '@/composables/useCookie';
import { COOKIE_KEY } from '@/constant';
import { defineAppPlugin } from '@/main';
import throttle from '@/utils/throttle';

import type { ComputedRef, InjectionKey, Ref } from 'vue';

import useMutationLogin from '#server/useMutationLogin';
import useMutationLogout from '#server/useMutationLogout';

const secondsInMinute = 60;
const millisecondsInSecond = 1000;
const expiredTime = 10 * secondsInMinute; // 十分鐘
const throttleTime = 10 * millisecondsInSecond; // 十秒

const AUTH_INJECT_KEY: InjectionKey<{
  token: Ref<string | null>;
  message: ComputedRef<string>;
  idle: ComputedRef<boolean>;
  isLoginLoading: Ref<boolean>;
  isLogoutLoading: Ref<boolean>;
  mutateLogin: ReturnType<typeof useMutationLogin>['mutate'];
  mutateLogout: ReturnType<typeof useMutationLogout>['mutate'];
}> = Symbol();

export default defineAppPlugin(context => {
  const { app, $router } = context;

  const token = useCookie<string | null>(COOKIE_KEY.TOKEN, {
    default: () => null,
  });

  const accessToken = useCookie<string | null>(COOKIE_KEY.ACCESS_TOKEN, {
    default: () => null,
  });
  const refreshToken = useCookie<string | null>(COOKIE_KEY.REFRESH_TOKEN, {
    default: () => null,
  });

  const refreshExpired = useCookie<Date | null>(COOKIE_KEY.REFRESH_EXPIRED, {
    default: () => null,
  });
  const { mutate: mutateLogin, isLoading: isLoginLoading } = useMutationLogin({
    onSuccess(_, variables) {
      token.value = variables.code;
    },
  });

  const route = { name: 'auth' };
  const { mutate: mutateLogout, isLoading: isLogoutLoading } =
    useMutationLogout({
      onMutate() {
        preloadRouteComponents(route);
      },
      onSuccess() {
        token.value = null;
        accessToken.value = null;
        refreshToken.value = null;
        refreshExpired.value = null;
        $router.push(route);

        context.$message({
          type: 'success',
          message: '您已登出成功',
        });
      },
    });

  const now = useTimestamp();
  const { dec, count, reset: resetCount } = useCounter(expiredTime);

  const {
    idle,
    lastActive,
    reset: resetIdle,
  } = useIdle(expiredTime * millisecondsInSecond);

  const message = computed(() => {
    const minute = Math.floor(count.value / secondsInMinute);
    const second = count.value % secondsInMinute;
    return `${minute} 分 ${second} 秒後自動登出。`;
  });

  watch(
    () => Math.floor((now.value - lastActive.value) / millisecondsInSecond),
    value => {
      if (!token.value) return;

      if (value > 0) {
        dec();
      } else {
        resetCount();
      }
    }
  );

  watch(idle, value => {
    if (value) {
      mutateLogout(undefined);
      context.$message({
        type: 'error',
        message: '您在本系統已閒置超過10分鐘，若需繼續使用本系統請重新登入!',
      });
    }
  });

  const channel = !(typeof BroadcastChannel === 'undefined')
    ? new BroadcastChannel('auth')
    : null;

  if (channel) {
    const identifier = timestamp();
    const collection = ref(new Set());

    const shared = useCookie<string | null>('nks_message', {
      default: () => null,
    });

    watch(message, value => {
      if (lastValue(collection.value) !== identifier) return;
      shared.value = value;
    });

    if (!document.hidden) {
      channel.postMessage({
        type: 'control',
        payload: identifier,
      });
    } else {
      channel.postMessage({
        type: 'init',
      });
    }

    const handleControl = (identifier: number) => {
      collection.value.delete(identifier);
      collection.value.add(identifier);
    };

    const onMessage = ({
      data,
    }: MessageEvent<
      | { type: 'init' }
      | { type: 'control'; payload: number }
      | { type: 'synchronize'; payload: Array<number> }
      | { type: 'destroy'; payload: number }
    >) => {
      switch (data.type) {
        case 'init': {
          if (lastValue(collection.value) !== identifier) return;
          channel.postMessage({
            type: 'synchronize',
            payload: [...collection.value],
          });
          break;
        }
        case 'synchronize': {
          collection.value = new Set(data.payload);
          break;
        }
        case 'control': {
          resetIdle();
          resetCount();
          lastActive.value = timestamp();
          handleControl(data.payload);
          break;
        }
        case 'destroy': {
          collection.value.delete(data.payload);
          break;
        }
      }
    };

    const onFocus = () => {
      if (document.hidden) return;

      handleControl(identifier);

      channel.postMessage({
        type: 'control',
        payload: identifier,
      });
    };

    const onEvent = throttle(onFocus, throttleTime);

    const onBeforeunload = () => {
      collection.value.clear();

      channel.postMessage({
        type: 'destroy',
        payload: identifier,
      });

      channel.removeEventListener('message', onMessage);
      channel.close();

      if (!collection.value.size) {
        shared.value = null;
      }
    };

    channel.addEventListener('message', onMessage);
    useEventListener(document, 'visibilitychange', onEvent);
    useEventListener(window, 'focus', onEvent);
    useEventListener(window, 'mouseover', onEvent);
    useEventListener(window, 'mousedown', onEvent);
    useEventListener(window, 'resize', onEvent);
    useEventListener(window, 'keydown', onEvent);
    useEventListener(window, 'touchstart', onEvent);
    useEventListener(window, 'wheel', onEvent);
    useEventListener('beforeunload', onBeforeunload);
  }

  app.provide(AUTH_INJECT_KEY, {
    token,
    message,
    idle,
    isLoginLoading,
    isLogoutLoading,
    mutateLogin,
    mutateLogout,
  });
});

export function useAuthState() {
  const auth = inject(AUTH_INJECT_KEY);
  if (!auth) throw new Error('useAuth is called without provider.');
  return auth;
}

/**
 * 取得 Set 的最後一筆資料
 */
function lastValue<T>(set: Set<T>): T {
  let value;
  for (value of set);
  return value as T;
}
